import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../ui/dialog';
import { Button } from '../ui/button';
import { Label } from '../ui/label';
import { cn } from '../../lib/utils';
import { ArrowRight, RotateCcw, Loader2 } from 'lucide-react';

const SR_STATUSES = [
  { value: 'Backlog', color: 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300' },
  { value: 'Todo', color: 'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300' },
  { value: 'In Progress', color: 'bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300' },
  { value: 'Review', color: 'bg-purple-100 text-purple-700 dark:bg-purple-900/40 dark:text-purple-300' },
  { value: 'Done', color: 'bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300' },
];

// Default ADO states per process template
const ADO_PROCESS_TEMPLATES = {
  agile: {
    label: 'Agile',
    states: ['New', 'Active', 'Resolved', 'Closed', 'Removed'],
    defaults: { 'New': 'Backlog', 'Active': 'In Progress', 'Resolved': 'Review', 'Closed': 'Done', 'Removed': 'Done' },
  },
  scrum: {
    label: 'Scrum',
    states: ['New', 'Approved', 'Committed', 'Done', 'Removed'],
    defaults: { 'New': 'Backlog', 'Approved': 'Todo', 'Committed': 'In Progress', 'Done': 'Done', 'Removed': 'Done' },
  },
  cmmi: {
    label: 'CMMI',
    states: ['Proposed', 'Active', 'Resolved', 'Closed'],
    defaults: { 'Proposed': 'Backlog', 'Active': 'In Progress', 'Resolved': 'Review', 'Closed': 'Done' },
  },
  basic: {
    label: 'Basic',
    states: ['To Do', 'Doing', 'Done'],
    defaults: { 'To Do': 'Backlog', 'Doing': 'In Progress', 'Done': 'Done' },
  },
};

const ADO_STATE_COLORS = {
  'New': '#007acc',
  'Active': '#ff9d00',
  'Resolved': '#9b59b6',
  'Closed': '#339933',
  'Removed': '#999',
  'Approved': '#007acc',
  'Committed': '#ff9d00',
  'Done': '#339933',
  'Proposed': '#007acc',
  'To Do': '#007acc',
  'Doing': '#ff9d00',
};

const AdoStatusMappingModal = ({ open, onClose, currentMapping, onSave, colors, saving }) => {
  const [template, setTemplate] = useState('agile');
  const [mapping, setMapping] = useState({});

  useEffect(() => {
    if (open) {
      if (currentMapping && Object.keys(currentMapping).length > 0) {
        setMapping(currentMapping);
        // Detect which template matches the current states
        const currentStates = Object.keys(currentMapping);
        for (const [key, tmpl] of Object.entries(ADO_PROCESS_TEMPLATES)) {
          if (tmpl.states.every(s => currentStates.includes(s))) {
            setTemplate(key);
            break;
          }
        }
      } else {
        setMapping({ ...ADO_PROCESS_TEMPLATES.agile.defaults });
      }
    }
  }, [open, currentMapping]);

  const handleTemplateChange = (newTemplate) => {
    setTemplate(newTemplate);
    setMapping({ ...ADO_PROCESS_TEMPLATES[newTemplate].defaults });
  };

  const handleStateChange = (adoState, srStatus) => {
    setMapping(prev => ({ ...prev, [adoState]: srStatus }));
  };

  const handleReset = () => {
    setMapping({ ...ADO_PROCESS_TEMPLATES[template].defaults });
  };

  const handleSave = () => {
    onSave(mapping);
  };

  const currentTemplate = ADO_PROCESS_TEMPLATES[template];

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className={cn("sm:max-w-lg", colors?.bgCard)} data-testid="ado-status-mapping-modal">
        <DialogHeader>
          <DialogTitle className={colors?.textPrimary}>Status Mapping</DialogTitle>
          <DialogDescription className={colors?.textMuted}>
            Map Azure DevOps work item states to StackRanked feature statuses. When a work item changes state in ADO, the linked feature will automatically update.
          </DialogDescription>
        </DialogHeader>

        {/* Process Template Selector */}
        <div className="mb-4">
          <Label className={cn("text-xs font-medium mb-2 block", colors?.textMuted)}>
            ADO Process Template
          </Label>
          <div className="flex gap-1.5">
            {Object.entries(ADO_PROCESS_TEMPLATES).map(([key, tmpl]) => (
              <button
                key={key}
                onClick={() => handleTemplateChange(key)}
                className={cn(
                  "px-3 py-1.5 text-xs rounded-md font-medium transition-colors",
                  template === key
                    ? "bg-blue-600 text-white"
                    : cn("border", colors?.borderPrimary, colors?.textSecondary, "hover:bg-gray-100 dark:hover:bg-gray-800")
                )}
                data-testid={`ado-template-${key}`}
              >
                {tmpl.label}
              </button>
            ))}
          </div>
        </div>

        {/* Mapping Grid */}
        <div className="space-y-2">
          <div className="grid grid-cols-[1fr_32px_1fr] gap-2 items-center mb-1">
            <Label className={cn("text-xs font-medium", colors?.textMuted)}>Azure DevOps State</Label>
            <div />
            <Label className={cn("text-xs font-medium", colors?.textMuted)}>StackRanked Status</Label>
          </div>

          {currentTemplate.states.map(adoState => (
            <div key={adoState} className="grid grid-cols-[1fr_32px_1fr] gap-2 items-center" data-testid={`mapping-row-${adoState.replace(/\s/g, '-')}`}>
              {/* ADO State pill */}
              <div className="flex items-center gap-2">
                <span
                  className="w-2.5 h-2.5 rounded-full flex-shrink-0"
                  style={{ backgroundColor: ADO_STATE_COLORS[adoState] || '#999' }}
                />
                <span className={cn("text-sm font-medium", colors?.textPrimary)}>{adoState}</span>
              </div>

              {/* Arrow */}
              <ArrowRight className={cn("w-4 h-4 mx-auto", colors?.textMuted)} />

              {/* SR Status selector */}
              <select
                value={mapping[adoState] || 'Backlog'}
                onChange={(e) => handleStateChange(adoState, e.target.value)}
                className={cn(
                  "w-full px-2.5 py-1.5 rounded-md border text-sm font-medium",
                  colors?.borderPrimary, colors?.bgSecondary, colors?.textPrimary,
                  "focus:outline-none focus:ring-2 focus:ring-blue-500"
                )}
                data-testid={`mapping-select-${adoState.replace(/\s/g, '-')}`}
              >
                {SR_STATUSES.map(s => (
                  <option key={s.value} value={s.value}>{s.value}</option>
                ))}
              </select>
            </div>
          ))}
        </div>

        {/* Preview */}
        <div className={cn("mt-4 p-3 rounded-lg text-xs", colors?.bgTertiary)}>
          <p className={cn("font-medium mb-2", colors?.textSecondary)}>Preview</p>
          <div className="space-y-1">
            {currentTemplate.states.map(adoState => {
              const srStatus = mapping[adoState] || 'Backlog';
              const srConfig = SR_STATUSES.find(s => s.value === srStatus);
              return (
                <div key={adoState} className="flex items-center gap-2">
                  <span className={colors?.textMuted}>When ADO →</span>
                  <span className="font-medium" style={{ color: ADO_STATE_COLORS[adoState] }}>{adoState}</span>
                  <span className={colors?.textMuted}>then SR →</span>
                  <span className={cn("px-1.5 py-0.5 rounded text-xs font-medium", srConfig?.color)}>
                    {srStatus}
                  </span>
                </div>
              );
            })}
          </div>
        </div>

        <DialogFooter className="flex items-center justify-between mt-4">
          <Button variant="ghost" size="sm" onClick={handleReset} className={colors?.textMuted} data-testid="ado-mapping-reset">
            <RotateCcw className="w-3.5 h-3.5 mr-1" /> Reset to Defaults
          </Button>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={() => onClose(false)} data-testid="ado-mapping-cancel">
              Cancel
            </Button>
            <Button size="sm" onClick={handleSave} disabled={saving} className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="ado-mapping-save">
              {saving ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : null}
              Save Mapping
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default AdoStatusMappingModal;
